import { useHandler } from "@redotech/react-util/hook";
import type { TElement, Value } from "platejs";
import { createSlateEditor } from "platejs";
import { usePlateEditor } from "platejs/react";
import { serializeHtml } from "platejs/static";
import {
  type EditorToolbarMode,
  getBasePluginsForMode,
  getPluginsForMode,
} from "./editor-plugins";

const createEmptyValue = (): Value => [
  { type: "p", children: [{ text: "" }] } as TElement,
];

export function htmlToPlateContent(html: string): Value {
  const div = document.createElement("div");
  div.innerHTML = html;

  const blocks: Value = [];
  const childNodes = div.childNodes;

  if (childNodes.length === 0) {
    return [{ type: "p", children: [{ text: html }] } as TElement];
  }

  for (const node of childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      if (text.trim()) {
        blocks.push({ type: "p", children: [{ text }] } as TElement);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      blocks.push({
        type: "p",
        children: [{ text: el.textContent ?? "" }],
      } as TElement);
    }
  }

  return blocks.length > 0 ? blocks : createEmptyValue();
}

export interface UseMessageEditorResult {
  editor: ReturnType<typeof usePlateEditor>;
  isEmpty: boolean;
  clearContent: () => void;
  getPlainText: () => string;
  setValue: (value: Value) => void;
  getSerializedValue: () => string;
  getHtml: () => Promise<string>;
}

export function useMessageEditor(
  mode: EditorToolbarMode = "email",
  id?: string,
): UseMessageEditorResult {
  const plugins = getPluginsForMode(mode);

  const editor = usePlateEditor({
    plugins,
    value: createEmptyValue(),
    id: id || `message-editor-${mode}`,
  });

  const isEmpty = editor.api.isEmpty();

  const clearContent = useHandler(() => {
    editor.tf.setValue(createEmptyValue());
  });

  const getPlainText = useHandler(() => {
    return editor.api.string([]);
  });

  const setValue = useHandler((value: Value) => {
    editor.tf.setValue(value);
  });

  const getSerializedValue = useHandler((): string => {
    return JSON.stringify(editor.children);
  });

  const getHtml = useHandler(async (): Promise<string> => {
    const staticEditor = createSlateEditor({
      plugins: getBasePluginsForMode(mode),
      value: editor.children,
    });
    return await serializeHtml(staticEditor, {
      stripClassNames: true,
      stripDataAttributes: true,
    });
  });

  return {
    editor,
    isEmpty,
    clearContent,
    getPlainText,
    setValue,
    getSerializedValue,
    getHtml,
  };
}
